import React, { useState } from "react";
import { Card, Row, Col, Spin, Button, Segmented } from "antd";
import { LoadingOutlined, FilterOutlined } from "@ant-design/icons";
import NumberFormat from "react-number-format";
import dayjs from "dayjs";
import useSupportOfficerData from "../hooks/useSupportOfficerData";
import { roundDisplayPrice } from "../../../utils/priceHelpers";

const SupportOfficerHomeContent = ({
  userRole = "SO",
  useOwnLayout = false,
  showDateFilter = true,
}) => {
  const { progressData, salesData, dashboardStats, loading, fetchAllData } =
    useSupportOfficerData();
  const [selectedFilter, setSelectedFilter] = useState("Today");

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const handleFilterChange = (value) => {
    setSelectedFilter(value);
    const today = dayjs();
    let dateFrom, dateTo;

    switch (value) {
      case "Today":
        dateFrom = today.startOf("day");
        dateTo = today.endOf("day");
        break;
      case "This Week":
        dateFrom = today.startOf("week");
        dateTo = today.endOf("week");
        break;
      case "This Month":
        dateFrom = today.startOf("month");
        dateTo = today.endOf("month");
        break;
      case "Till Date":
        dateFrom = dayjs("2020-01-01");
        dateTo = today;
        break;
      default:
        dateFrom = today.startOf("day");
        dateTo = today.endOf("day");
    }

    fetchAllData(dateFrom.format("YYYY-MM-DD"), dateTo.format("YYYY-MM-DD"));
  };

  const getTitle = () => {
    switch (userRole) {
      case "SM":
        return "Support Manager Portal";
      case "SE":
        return "Support Executive Portal";
      default:
        return "Support Officer Portal";
    }
  };

  const getDescription = () => {
    switch (userRole) {
      case "SM":
        return "Monitor your team's performance";
      case "SE":
        return "Oversee all support operations";
      default:
        return "Monitor your assigned restaurants";
    }
  };

  return (
    <Spin spinning={loading} indicator={antIcon}>
      <div className={useOwnLayout ? "pb-40" : ""}>
        <div className="main_container_title_div mb-5 mt-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
            {getTitle()}
          </h1>
          <p className="text-gray-600 text-xl sm:text-base">
            {getDescription()}
          </p>
        </div>

        <div className="mb-6">
          <Segmented
            options={["Today", "This Week", "This Month", "Till Date"]}
            value={selectedFilter}
            onChange={handleFilterChange}
          />
        </div>

        <div className="space-y-3 mb-6">
          <Card bordered={false} className="p-4">
            <p className="text-sm text-gray-600">Total Orders</p>
            <p className="text-3xl font-bold text-gray-900">
              {dashboardStats?.ordersCountToday?.toLocaleString() || 0}
            </p>
          </Card>

          <Card bordered={false} className="p-4">
            <p className="text-sm text-gray-600">Businesses with Sales</p>
            <p className="text-3xl font-bold text-gray-900">
              {dashboardStats?.businessesWithSalesToday?.toLocaleString() || 0}
            </p>
          </Card>

          <Card bordered={false} className="p-4">
            <p className="text-sm text-gray-600">Today's Sales</p>
            <p className="text-3xl font-bold text-gray-900">
              <NumberFormat
                thousandSeparator={true}
                prefix={"₦"}
                className="numeric"
                displayType={"text"}
                value={parseFloat(dashboardStats?.todaySalesNGN || 0).toFixed(
                  2,
                )}
              />
            </p>
          </Card>
        </div>

        <Card bordered={false} className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">
            Order Status Breakdown
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600">Waiting</p>
              <p className="text-2xl font-bold text-gray-900">
                {progressData?.stats?.waiting?.count || 0}
              </p>
              <p className="text-xs text-gray-500">
                {progressData?.stats?.waiting?.percentage || 0}%
              </p>
            </div>

            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600">Accept</p>
              <p className="text-2xl font-bold text-gray-900">
                {progressData?.stats?.accept?.count || 0}
              </p>
              <p className="text-xs text-gray-500">
                {progressData?.stats?.accept?.percentage || 0}%
              </p>
            </div>

            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600">Ready</p>
              <p className="text-2xl font-bold text-gray-900">
                {progressData?.stats?.ready?.count || 0}
              </p>
              <p className="text-xs text-gray-500">
                {progressData?.stats?.ready?.percentage || 0}%
              </p>
            </div>

            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600">Pickup</p>
              <p className="text-2xl font-bold text-gray-900">
                {progressData?.stats?.pickup?.count || 0}
              </p>
              <p className="text-xs text-gray-500">
                {progressData?.stats?.pickup?.percentage || 0}%
              </p>
            </div>

            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600">In Transit</p>
              <p className="text-2xl font-bold text-gray-900">
                {progressData?.stats?.inTransit?.count || 0}
              </p>
              <p className="text-xs text-gray-500">
                {progressData?.stats?.inTransit?.percentage || 0}%
              </p>
            </div>

            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600">Delivered</p>
              <p className="text-2xl font-bold text-gray-900">
                {progressData?.stats?.delivered?.count || 0}
              </p>
              <p className="text-xs text-gray-500">
                {progressData?.stats?.delivered?.percentage || 0}%
              </p>
            </div>
          </div>
        </Card>

        <Card bordered={false} className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">Business Status</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600 mb-1">Open</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboardStats?.businessStatus?.open || 0}
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600 mb-1">Closed</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboardStats?.businessStatus?.closed || 0}
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600 mb-1">Inactive</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboardStats?.businessStatus?.inactive || 0}
              </p>
            </div>
          </div>
        </Card>

        <Card bordered={false}>
          <h3 className="font-semibold text-gray-900 mb-4">Sales Summary</h3>
          <Row gutter={16}>
            <Col xs={12} sm={8}>
              <div className="p-4 bg-gray-50 rounded">
                <p className="text-xs text-gray-600 mb-1">Approved Orders</p>
                <p className="text-2xl font-bold text-gray-900">
                  {salesData?.approved?.toLocaleString() || 0}
                </p>
              </div>
            </Col>
            <Col xs={12} sm={8}>
              <div className="p-4 bg-gray-50 rounded">
                <p className="text-xs text-gray-600 mb-1">This Week</p>
                <p className="text-2xl font-bold text-gray-900">
                  ₦{roundDisplayPrice(salesData?.salesThisWeekNGN)}
                </p>
              </div>
            </Col>
            <Col xs={12} sm={8}>
              <div className="p-4 bg-gray-50 rounded">
                <p className="text-xs text-gray-600 mb-1">This Month</p>
                <p className="text-2xl font-bold text-gray-900">
                  ₦{roundDisplayPrice(salesData?.salesThisMonthNGN)}
                </p>
              </div>
            </Col>
          </Row>
        </Card>
      </div>
    </Spin>
  );
};

export default SupportOfficerHomeContent;
